import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Button, Flex, Select, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	GetInvestorTransactionDetailsRes,
	VestingTermSimplifiedRes,
} from '@dealstack/shared/lib/generated';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import {
	DATE_FORMATS,
	formatStringDateToUI,
	parseBEDateIgnoreTimezone,
	parseUiDateDdMmYyyy,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { DateInput } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	vestingTerms: VestingTermSimplifiedRes[];
	loading?: boolean;
	selectedTransaction: GetInvestorTransactionDetailsRes | undefined;
	onVestingTermApply: (vestingTermId: string, transactionId: string) => void;
	onVestingTermRemove: (transactionId: string) => void;
	onVestingDateChange: (transactionId: string, startDate: Date | null) => void;
}

export const TransactionVestingTermsDrawer = ({
	opened,
	onClose,
	vestingTerms,
	loading,
	selectedTransaction,
	onVestingTermApply,
	onVestingTermRemove,
	onVestingDateChange,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [dateInputValue, setDateInputValue] = useState<Date | null>(null);

	useEffect(() => {
		if (selectedTransaction?.vestingStartDate) {
			setDateInputValue(parseBEDateIgnoreTimezone(selectedTransaction.vestingStartDate));
		} else {
			setDateInputValue(null);
		}
	}, [selectedTransaction?.vestingStartDate, selectedTransaction?.id]);

	const formattedCompletedDate = formatStringDateToUI(
		selectedTransaction?.completedDate,
		DATE_FORMATS.CALENDAR_DATE
	);

	const vestingStartDateWarnings = useMemo(
		() => [
			...(selectedTransaction?.vestingTermId
				? []
				: [tMipManage('vesting_terms_drawer.start_date_missing_vesting_term')]),
			...(selectedTransaction?.vestingStartDate || !selectedTransaction?.vestingTermId
				? []
				: [
						tMipManage('vesting_terms_drawer.vesting_default_start_date_info', {
							defaultDate: formattedCompletedDate,
						}),
					]),
		],
		[
			formattedCompletedDate,
			selectedTransaction?.vestingStartDate,
			selectedTransaction?.vestingTermId,
			tMipManage,
		]
	);

	const handleVestingStartDateChange = (date: Date | null) => {
		setDateInputValue(date);

		onVestingDateChange(selectedTransaction!.id, date);
	};

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			transitionProps={drawerTransitionProps}
			position="right"
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VESTING_DRAWER}
			title={<Text size="B4Bold">{tMipManage('vesting_terms_drawer.title')}</Text>}
			body={
				<Flex direction="column" h="100%" gap="1rem">
					{loading ? <InlineLoader /> : null}

					<Flex align="flex-end" gap="0.5rem">
						<Select
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							data={
								vestingTerms?.map((vestingTerm) => ({
									label: vestingTerm.name,
									value: vestingTerm.id,
								})) ?? []
							}
							w="100%"
							size="md"
							label={
								<Text fz="md" c="dark.0" fw={400} mb="0.25rem">
									{tMipManage('investors.expanded_row.vesting_term')}
								</Text>
							}
							value={selectedTransaction?.vestingTermId}
							onChange={(vestingTermId) =>
								selectedTransaction &&
								onVestingTermApply(vestingTermId ?? '', selectedTransaction.id)
							}
							placeholder={tMipManage('investors.select')}
						/>
						{!!selectedTransaction?.vestingTermId && (
							<Button
								variant="subtle"
								color="red"
								disabled={loading}
								h="40px"
								style={{ flexShrink: 0 }}
								onClick={() =>
									selectedTransaction &&
									onVestingTermRemove(selectedTransaction.id)
								}
							>
								{tMipManage('investors.remove_short')}
							</Button>
						)}
					</Flex>

					<Flex direction="column" gap="0.25rem">
						<DateInput
							label={
								<Text fz="md" c="dark.0" fw={400} mb="0.25rem">
									{tMipManage('vesting_terms_drawer.vesting_start_date_label')}
								</Text>
							}
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VESTING_START_DATE_INPUT
							}
							dateParser={parseUiDateDdMmYyyy}
							value={dateInputValue}
							size="md"
							disabled={loading || !selectedTransaction?.vestingTermId}
							clearable
							placeholder={tMipManage('investors.select')}
							valueFormat="DD/MM/YYYY"
							onChange={handleVestingStartDateChange}
						/>
						<div>
							{vestingStartDateWarnings.map((warning, index) => (
								<Text key={`vestingStartDateWarning${index}`} fz="xs">
									{warning}
								</Text>
							))}
						</div>
					</Flex>
				</Flex>
			}
		/>
	);
};
